import { useEffect } from "react";
import { ElementRu, UnitClassRu } from "../types/army";
import { OpponentEntry, ScoutReport } from "../types/marches";
import { formatNumber } from "../lib/format";
import { hapticImpact } from "../lib/telegram";
import { CurrencyValue } from "./CurrencyIcon";
import { UnitClassIcons } from "./icons";
import { GiPerson } from "react-icons/gi";

interface Props {
  opponent: OpponentEntry;
  report: ScoutReport | null;
  manaCurrent: number;
  scoutCost: number;
  loading: boolean;
  error: string | null;
  onScout: () => void;
  onAttack: () => void;
  onBack: () => void;
}

const SCOUT_MANA_COST = 50;

export function ScoutView({
  opponent,
  report,
  manaCurrent,
  scoutCost,
  loading,
  error,
  onScout,
  onAttack,
  onBack,
}: Props) {
  useEffect(() => {
    if (!report && manaCurrent >= scoutCost && !loading) {
      onScout();
    }
    // run only once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={onBack}
          className="rounded-lg bg-tg-secondaryBg px-3 py-1.5 text-sm"
        >
          ←
        </button>
        <div className="font-medium">{opponent.displayName}</div>
      </div>

      <div className="px-3 text-xs text-tg-hint">
        {ElementRu[opponent.element]} · Замок Lvl {opponent.castleLevel}
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto px-3 py-3">
        {error && (
          <div className="rounded-lg bg-tg-destructive/20 p-2 text-sm text-tg-destructive">
            {error}
          </div>
        )}

        {!report && !loading && (
          <div className="rounded-xl bg-tg-secondaryBg p-3 text-sm">
            <div className="mb-2 flex flex-wrap items-center gap-1">
              <span>Разведка стоит</span>
              <CurrencyValue kind="mana" value={SCOUT_MANA_COST} />
              <span>· у тебя</span>
              <CurrencyValue kind="mana" value={manaCurrent} />
            </div>
            <button
              type="button"
              disabled={manaCurrent < SCOUT_MANA_COST}
              onClick={() => {
                hapticImpact("medium");
                onScout();
              }}
              className="w-full rounded-lg bg-tg-button px-3 py-2 text-tg-buttonText disabled:opacity-40"
            >
              Разведать
            </button>
          </div>
        )}

        {loading && (
          <div className="rounded-xl bg-tg-secondaryBg p-3 text-center text-sm text-tg-hint">
            Шпионы пошли…
          </div>
        )}

        {report && (
          <div className="rounded-xl bg-tg-secondaryBg p-3">
            <div className="mb-2 text-xs uppercase tracking-wide text-tg-hint">
              Разведка
            </div>
            <div className="space-y-1.5 text-sm">
              <Stat label="Сила (≈)" value={formatNumber(report.approxTotalPower)} />
              <Stat label="Замок" value={`Lvl ${report.castleLevel}`} />
              <Stat label="Героев" value={String(report.heroCount)} />
              <Stat label="Щит" value={report.isShielded ? "Да" : "Нет"} />
            </div>

            {Object.keys(report.approxUnitsByClass).length > 0 && (
              <div className="mt-3 border-t border-tg-bg pt-2">
                <div className="mb-1 text-xs uppercase tracking-wide text-tg-hint">
                  Гарнизон (≈ по классам)
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {Object.entries(report.approxUnitsByClass).map(([cls, count]) => {
                    const Icon = UnitClassIcons[Number(cls)] ?? GiPerson;
                    return (
                      <div key={cls} className="flex items-center gap-1.5">
                        <Icon size={14} />
                        <span className="text-tg-hint">
                          {UnitClassRu[Number(cls)] ?? cls}:
                        </span>
                        <span className="tabular-nums">{formatNumber(count)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {report && !report.isShielded && (
          <button
            type="button"
            onClick={() => {
              hapticImpact("medium");
              onAttack();
            }}
            className="w-full rounded-lg bg-tg-button px-4 py-3 font-medium text-tg-buttonText"
          >
            Атаковать
          </button>
        )}
        {report?.isShielded && (
          <div className="rounded-lg bg-tg-secondaryBg p-2 text-center text-sm text-tg-hint">
            Цель под щитом — атаковать нельзя.
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-tg-hint">{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}
